import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { formatUS } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BreedingsPage() {
  const nav = useNavigate();
  const [breedings, setBreedings] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [form, setForm] = useState({ sire_id: "", dam_id: "", date: "", notes: "" });
  const [litterForm, setLitterForm] = useState({}); // bid -> { name, sex }
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    const [b, a] = await Promise.all([axios.get(`${API}/breedings`), axios.get(`${API}/animals`)]);
    setBreedings(b.data); setAnimals(a.data);
  };
  useEffect(() => { load(); }, []);

  const byId = Object.fromEntries(animals.map(a => [a.id, a]));
  const males = animals.filter(a => a.sex === "male");
  const females = animals.filter(a => a.sex === "female");

  const create = async (e) => {
    e.preventDefault();
    if (!form.sire_id || !form.dam_id) { toast.error("Pick a sire and a dam"); return; }
    try {
      await axios.post(`${API}/breedings`, form);
      toast.success("Breeding recorded");
      setForm({ sire_id: "", dam_id: "", date: "", notes: "" });
      load();
    } catch { toast.error("Could not record breeding"); }
  };

  const setLF = (bid, patch) =>
    setLitterForm((cur) => ({ ...cur, [bid]: { ...(cur[bid] || { name: "", sex: "male", expanded: false }), ...patch } }));

  const addLitter = async (bid) => {
    const lf = litterForm[bid] || { name: "", sex: "male" };
    if (!lf.name?.trim()) { toast.error("Name required"); return; }
    try {
      const payload = {
        name: lf.name,
        sex: lf.sex,
        registration: lf.registration || "",
        title: lf.title || "",
        dob: lf.dob || "",
        notes: lf.notes || "",
      };
      await axios.post(`${API}/breedings/${bid}/litter`, payload);
      toast.success(`Added ${lf.name}`);
      setLitterForm({ ...litterForm, [bid]: { name: "", sex: lf.sex, expanded: lf.expanded } });
      load();
    } catch { toast.error("Could not add to litter"); }
  };

  const delBreeding = async (bid) => {
    await axios.delete(`${API}/breedings/${bid}`);
    toast.success("Breeding deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-[var(--app-bg)]">
      <Toaster richColors position="top-right" />
      <header className="h-14 border-b border-[var(--app-border)] bg-white px-5 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/")} data-testid="back-library">
          <ChevronLeft className="w-4 h-4 mr-1" /> Library
        </Button>
        <div className="w-px h-7 bg-[var(--app-border)]" />
        <div className="font-display font-extrabold text-lg">Breedings</div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={create} className="bg-white border border-[var(--app-border)] rounded-md p-4 mb-6" data-testid="new-breeding-form">
          <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-3">Record a new breeding</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Sire (male)</Label>
              <Select value={form.sire_id} onValueChange={(v) => setForm({ ...form, sire_id: v })}>
                <SelectTrigger data-testid="new-breeding-sire"><SelectValue placeholder="Choose sire" /></SelectTrigger>
                <SelectContent>{males.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Dam (female)</Label>
              <Select value={form.dam_id} onValueChange={(v) => setForm({ ...form, dam_id: v })}>
                <SelectTrigger data-testid="new-breeding-dam"><SelectValue placeholder="Choose dam" /></SelectTrigger>
                <SelectContent>{females.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Date</Label>
              <Input type="date" className="font-mono-tech" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="new-breeding-date" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="new-breeding-notes" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-create-breeding">
              <Plus className="w-4 h-4 mr-1.5" /> Record breeding
            </Button>
          </div>
        </form>

        {breedings.length === 0 ? (
          <div className="text-center py-12 text-sm text-[var(--app-text-soft)] border border-dashed border-[var(--app-border)] rounded">
            No breedings recorded yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="breedings-list">
            {breedings.map((b) => {
              const sire = byId[b.sire_id], dam = byId[b.dam_id];
              const lf = litterForm[b.id] || { name: "", sex: "male", expanded: false };
              return (
                <div key={b.id} className="bg-white border border-[var(--app-border)] rounded-md p-4" data-testid={`breeding-${b.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <div className="font-display font-semibold">
                        <Link to={sire ? `/animals/${sire.id}` : "#"} className="hover:text-blue-600">{sire?.name || "?"}</Link>
                        {" × "}
                        <Link to={dam ? `/animals/${dam.id}` : "#"} className="hover:text-blue-600">{dam?.name || "?"}</Link>
                      </div>
                      <div className="text-xs text-[var(--app-text-soft)]">
                        {formatUS(b.date) || "—"}{b.notes ? ` · ${b.notes}` : ""}
                      </div>
                    </div>
                    <button onClick={() => setPendingDelete(b)} className="text-red-500 hover:text-red-700 p-1" aria-label="Delete breeding" data-testid={`delete-breeding-${b.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-3 pt-3 border-t border-[var(--app-border)]">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">
                      Litter ({b.litter_ids.length})
                    </div>
                    {b.litter_ids.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {b.litter_ids.map(lid => {
                          const a = byId[lid];
                          return a ? (
                            <Link key={lid} to={`/animals/${lid}`} className="px-2 py-1 text-xs bg-[var(--app-bg)] border border-[var(--app-border)] rounded hover:bg-white">
                              {a.name} <span className="text-[var(--app-text-soft)]">· {a.sex}</span>
                            </Link>
                          ) : null;
                        })}
                      </div>
                    )}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Add to litter</Label>
                        <Input value={lf.name} onChange={(e) => setLF(b.id, { name: e.target.value })} placeholder="Name" data-testid={`litter-name-${b.id}`} />
                      </div>
                      <Select value={lf.sex} onValueChange={(v) => setLF(b.id, { sex: v })}>
                        <SelectTrigger className="w-[110px]" data-testid={`litter-sex-${b.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setLF(b.id, { expanded: !lf.expanded })} data-testid={`litter-expand-${b.id}`}>
                        {lf.expanded ? "Less" : "More"}
                      </Button>
                      <Button onClick={() => addLitter(b.id)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`add-litter-${b.id}`}>
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    {lf.expanded && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid={`litter-details-${b.id}`}>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Registration #</Label>
                          <Input value={lf.registration || ""} onChange={(e) => setLF(b.id, { registration: e.target.value })} placeholder="optional" data-testid={`litter-registration-${b.id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Title</Label>
                          <Input value={lf.title || ""} onChange={(e) => setLF(b.id, { title: e.target.value })} placeholder="optional" data-testid={`litter-title-${b.id}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">DOB</Label>
                          <Input type="date" className="font-mono-tech" value={lf.dob || ""} onChange={(e) => setLF(b.id, { dob: e.target.value })} data-testid={`litter-dob-${b.id}`} />
                        </div>
                        <div className="md:col-span-3">
                          <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">Notes</Label>
                          <Input value={lf.notes || ""} onChange={(e) => setLF(b.id, { notes: e.target.value })} placeholder="optional" data-testid={`litter-notes-${b.id}`} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-breeding">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this breeding record?</AlertDialogTitle>
            <AlertDialogDescription>
              The breeding event will be removed. Any animals already added to the litter remain in your Library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={async () => { const b = pendingDelete; setPendingDelete(null); if (b) await delBreeding(b.id); }} data-testid="confirm-delete-breeding-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
